//-- Copyright (c) 2021 TMT Insights Group, LLCß
import { CSSProperties } from 'react';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { UserProfile } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { Grid } from '@mui/material';
import { getUserDisplayName } from '../../../_utils/utils';
import { selectUser } from '../../../redux/selectors/authSelectors';
import UnassignedUserDisplay from './UnassignedUserDisplay';
import { useAppSelector } from '@/redux/hooks';

export type UserDisplayProps = {
  user: Partial<UserProfile> | null;
  emptyText?: string;
  iconSize?: number;
  canAssign?: boolean;
  ellipsisOverflow?: boolean;
  iconStyle?: CSSProperties;
};

const UserDisplay = ({ user, emptyText = 'Unassigned', iconSize = 18, canAssign, ellipsisOverflow, iconStyle }: UserDisplayProps) => {
  const currentUser = useAppSelector(selectUser);
  const isMe = user?.id === currentUser?.id;

  const userIconDefaultProps = {
    id: 'userIcon',
    style: iconStyle,
    size: iconSize,
    height: iconSize
  };

  const unassignedIcon = <UserIcon {...userIconDefaultProps} icon="unassigned" />;
  const isInactive = user?.is_active === false;

  if (!user && !canAssign) return emptyText || 'Unassigned';
  if (!user) return <UnassignedUserDisplay iconStyle={iconStyle} />;

  const displayName = getUserDisplayName(user);

  const getIcon = () => {
    if (isMe) return <UserIcon {...userIconDefaultProps} icon="user_filled_star" />;
    else if (isInactive) return <UserIcon {...userIconDefaultProps} icon="fa-solid:user-alt-slash" size={iconSize - 3} height={iconSize - 3} />;
    else if (displayName?.includes('Unassigned')) return unassignedIcon;
    else return <UserIcon {...userIconDefaultProps} icon="user_filled" />;
  };

  return (
    <Wrapper me={`${isMe}`} inactive={+isInactive} container>
      {getIcon()}
      <DisplayName ellipsisOverflow={ellipsisOverflow}>{displayName}</DisplayName>
    </Wrapper>
  );
};

export default UserDisplay;

const Wrapper = styled(Grid)<{
  me?: string;
  inactive: number;
}>`
  line-height: 1.5;
  font-size: 0.88rem;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  opacity: ${({ inactive }) => (inactive ? 0.33 : 1)};
  font-style: ${({ inactive }) => (inactive ? 'italic' : 'normal')};

  & #userIcon {
    color: ${({ theme, me }) => (me === 'true' ? theme.palette.primary.main : 'inherit')};
  }
`;

const UserIcon = styled(PolarisIcon)<{ size: number }>`
  margin-right: ${({ theme }) => theme.spacing(0.5)};
  line-height: 1px;
  opacity: 0.66;
  position: relative;
  min-width: ${({ size }) => size}px;
  min-height: ${({ size }) => size}px;
`;

const DisplayName = styled.span<{ ellipsisOverflow?: boolean }>`
  white-space: nowrap;
  ${(props) =>
    props.ellipsisOverflow
      ? `
    max-width: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
  `
      : null};
`;
